/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";

import type { FileUploadConfig } from "./useFileUploadConfig";

import { simplifyMimeType } from "@/utils/attachmentUtils";

export const FILES_WITHOUT_MIME: Record<string, string> = {
  md: "text/markdown",
};

export const normalizeFile = (file: File): File => {
  if (file.type) return file;

  const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
  const mimeType =
    FILES_WITHOUT_MIME[fileExtension] || "application/octet-stream";

  return new File([file], file.name, { type: mimeType });
};

const validateFile = (
  file: File,
  config: FileUploadConfig,
  handlers?: FileErrorHandlers
): boolean => {
  if (config.file_types.length > 0 && !config.file_types.includes(file.type)) {
    const simplifiedType = simplifyMimeType(file.type);
    const message = `File "${file.name}" type (${simplifiedType}) is not supported`;
    handlers?.onFileNotSupported?.(file, message);
    return false;
  }

  if (file.size > config.file_size_limit) {
    const message = `File ${file.name} size exceeds the limit of ${
      config.file_size_limit / (1024 * 1024)
    } MB`;
    handlers?.onSizeLimitExceed?.(file, message);
    return false;
  }

  const sanitizedFileName = file.name.toLowerCase().endsWith(".pdf")
    ? file.name.slice(0, -4)
    : file.name;

  if (
    config.file_name_suffixes.length > 0 &&
    !config.file_name_suffixes.some((suffix) =>
      sanitizedFileName.endsWith(suffix)
    )
  ) {
    const message = `File ${
      file.name
    } name should end with one of the following suffixes: ${config.file_name_suffixes.join(
      ", "
    )}`;
    handlers?.onFileNameSuffixMismatch?.(file, message);
    return false;
  }

  return true;
};

const processFileEntry = (
  entry: FileSystemFileEntry,
  parentFolderPath: string,
  onFilesDrop: (files: File[]) => void
) => {
  entry.file((file: File) => {
    const normalizedFile = normalizeFile(file);
    (normalizedFile as any)["relativePath"] =
      `${parentFolderPath}/${file.name}`;
    onFilesDrop([normalizedFile]);
  });
};

const processDirectoryEntry = (
  entry: FileSystemDirectoryEntry,
  parentFolderPath: string,
  onFilesDrop: (files: File[]) => void
) => {
  const reader = entry.createReader();
  reader.readEntries((entries) => {
    entries.forEach((subEntry) => {
      const newFolderPath = parentFolderPath
        ? `${parentFolderPath}/${entry.name}`
        : entry.name;
      readFolderContents(subEntry, newFolderPath, onFilesDrop);
    });
  });
};

const readFolderContents = (
  entry: FileSystemDirectoryEntry | FileSystemEntry | FileSystemFileEntry,
  parentFolderPath: string,
  onFilesDrop: (files: File[]) => void
) => {
  if (entry.isDirectory) {
    processDirectoryEntry(
      entry as FileSystemDirectoryEntry,
      parentFolderPath,
      onFilesDrop
    );
  } else if (entry.isFile) {
    processFileEntry(
      entry as FileSystemFileEntry,
      parentFolderPath,
      onFilesDrop
    );
  }
};

const processDataTransferItem = (
  item: DataTransferItem,
  config: FileUploadConfig,
  onFilesDrop: (files: File[]) => void,
  handlers?: FileErrorHandlers
) => {
  if (item.kind === "file") {
    const entry = item.webkitGetAsEntry();
    if (!entry) return;

    if (entry.isDirectory) {
      if (!config.allow_folder_upload) {
        const message = "Folder upload is not allowed";
        handlers?.onFolderUploadNotAllowed?.(message);
        return;
      }
      readFolderContents(entry, "", onFilesDrop);
    } else {
      const file = item.getAsFile();
      if (!file) return;

      const normalizedFile = normalizeFile(file);
      if (validateFile(normalizedFile, config, handlers)) {
        onFilesDrop([normalizedFile]);
      }
    }
  }
};

const processFileList = (
  files: FileList,
  config: FileUploadConfig,
  onFilesDrop: (files: File[]) => void,
  handlers?: FileErrorHandlers
) => {
  const validFiles = Array.from(files)
    .map(normalizeFile)
    .filter((file) => validateFile(file, config, handlers));

  if (validFiles.length > 0) {
    onFilesDrop(validFiles);
  }
};

export const useFileHandler = (
  onFilesDrop: (files: File[]) => void,
  config: FileUploadConfig,
  handlers?: FileErrorHandlers
) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const folderInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.items) {
      const collectedFiles: File[] = [];

      const batchCallback = (files: File[]) => {
        collectedFiles.push(...files);
      };

      Array.from(e.dataTransfer.items).forEach((item) => {
        processDataTransferItem(item, config, batchCallback, handlers);
      });

      if (collectedFiles.length > 0) {
        onFilesDrop(collectedFiles);
      }
    } else {
      processFileList(e.dataTransfer.files, config, onFilesDrop, handlers);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFileList(e.target.files, config, onFilesDrop, handlers);
      e.target.value = "";
    }
  };

  const handleInputClick = (type: "file" | "folder") => {
    const inputRef = type === "file" ? fileInputRef : folderInputRef;
    inputRef.current?.click();
  };

  return {
    isDragging,
    fileInputRef,
    folderInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    handleFileInputClick: handleInputClick,
  };
};

export type FileErrorHandlers = {
  onSizeLimitExceed?: (file: File, message: string) => void;
  onFileNotSupported?: (file: File, message: string) => void;
  onFileNameSuffixMismatch?: (file: File, message: string) => void;
  onFolderUploadNotAllowed?: (message: string) => void;
};
